// One counterparty, as the two things it is (TX-US-0042): a case moving through onboarding, and
// an entry in a catalogue. In order: its basic details; the onboarding path, with the sheet of the
// stage open beneath it - the KYC review (TX-US-0035), the credit assessment (TX-US-0027), the
// legal review - and the actions at its end; its comments (TX-US-0037); the catalogue's tabs,
// Details, Quotes and Ratings (TX-US-0040), which take no part in onboarding; and its files
// (TX-US-0033).
//
// A sheet or a tab that is not open is hidden, not unmounted: a draft is still there on return,
// and both gate sheets are read once, so the path knows what each approval waits on - and where
// Compliance's sends the counterparty - from the first render.

import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import { getCounterparty } from '../api.js';
import { useMe } from '../MeContext.js';
import {
  COUNTERPARTY_COMMENT_ROLES,
  COUNTERPARTY_DOCUMENT_ROLES,
  COUNTERPARTY_QUOTE_ROLES,
  COUNTERPARTY_RATING_ROLES,
  holdsAny,
} from '../roles.js';
import CounterpartyComments from './comments/CounterpartyComments.jsx';
import CounterpartyDocuments from './documents/CounterpartyDocuments.jsx';
import BasicDetails from './profile/BasicDetails.jsx';
import DetailsTab from './profile/DetailsTab.jsx';
import OnboardingPath from './profile/OnboardingPath.jsx';
import { TABS, tabFrom, tabSearch } from './profile/tabs.js';
import CounterpartyQuotes from './quotes/CounterpartyQuotes.jsx';
import CounterpartyRatings from './ratings/CounterpartyRatings.jsx';

export default function CounterpartyDetail() {
  const { counterpartyId } = useParams();
  const [searchParams] = useSearchParams();
  const me = useMe();

  const [record, setRecord] = useState(null);
  const [error, setError] = useState(null);
  // The two sheets as last read or saved: what each gate is still waiting on.
  const [kyc, setKyc] = useState(null);
  const [credit, setCredit] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getCounterparty(counterpartyId)
      .then((found) => {
        if (!cancelled) setRecord(found);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [counterpartyId]);

  if (error) return <p className="error">{error}</p>;
  if (!record) return <p className="muted">Loading…</p>;

  const tab = tabFrom(searchParams);

  return (
    <div className="counterparty-profile">
      <BasicDetails record={record} />

      <OnboardingPath record={record} sheets={{ kyc, credit }} onKyc={setKyc} onCredit={setCredit}
                      onChanged={setRecord} />

      <section className="profile-panel">
        <CounterpartyComments counterpartyId={record.counterparty_id}
                              canComment={holdsAny(me, COUNTERPARTY_COMMENT_ROLES)} />
      </section>

      <section className="profile-panel">
        <nav className="tabs" aria-label="Counterparty sections">
          {TABS.map(({ key, label }) => (
            <Link key={key} to={{ search: tabSearch(searchParams, key) }}
                  aria-current={tab === key ? 'page' : undefined}>
              {label}
            </Link>
          ))}
        </nav>

        <div className="profile-tab" hidden={tab !== 'details'}>
          <DetailsTab record={record} />
        </div>
        <div className="profile-tab" hidden={tab !== 'quotes'}>
          <CounterpartyQuotes counterpartyId={record.counterparty_id} categories={record.categories}
                              canQuote={holdsAny(me, COUNTERPARTY_QUOTE_ROLES)} myEmail={me?.email} />
        </div>
        <div className="profile-tab" hidden={tab !== 'ratings'}>
          <CounterpartyRatings counterpartyId={record.counterparty_id}
                               canRate={holdsAny(me, COUNTERPARTY_RATING_ROLES)}
                               onRated={(summary) => setRecord((current) => ({ ...current, ...summary }))} />
        </div>
      </section>

      <section className="profile-panel">
        <CounterpartyDocuments counterpartyId={record.counterparty_id}
                               canEdit={holdsAny(me, COUNTERPARTY_DOCUMENT_ROLES)} />
      </section>

      <p><Link to="/counterparties">Back to the register</Link></p>
    </div>
  );
}
